import Phaser from 'phaser';
import { PALETTE } from '../config';
import { createPortrait, type AthleteView } from '../game/AthleteView';
import { AudioEngine } from '../game/AudioEngine';
import { findAthlete } from '../game/data/athletes';
import { countryFlag } from '../game/data/flags';
import { t, disciplineLabel } from '../game/i18n';
import { formatMetric } from '../game/data/metricFormat';
import { SaveManager } from '../game/SaveManager';
import type { DisciplineResult } from '../game/types';

/**
 * World-record celebration: a spectacular full-screen moment that projects the record-setting
 * athlete (in-engine, works for every animal) before handing off to Results. A hero Higgsfield
 * clip can be layered on top per star athlete later — see tryHeroClip().
 */
export class RecordCelebration extends Phaser.Scene {
  private rig?: AthleteView;
  private rays?: Phaser.GameObjects.Graphics;
  private result!: DisciplineResult;
  private advanced = false;

  constructor() {
    super('RecordCelebration');
  }

  init(result: DisciplineResult): void {
    this.result = result;
    this.advanced = false;
  }

  create(): void {
    const w = this.scale.width, h = this.scale.height, cx = w / 2, cy = h / 2;
    const s = Phaser.Math.Clamp(h / 720, 0.6, 1.6);

    // Dramatic dark backdrop with a warm spotlight pool behind the athlete.
    this.cameras.main.setBackgroundColor('#070a16');
    const bg = this.add.graphics().setDepth(-10);
    const c1 = Phaser.Display.Color.IntegerToColor(0x1a2342);
    const c2 = Phaser.Display.Color.IntegerToColor(0x070a16);
    for (let i = 10; i >= 0; i--) {
      const t = i / 10;
      const ci = Phaser.Display.Color.Interpolate.ColorWithColor(c1, c2, 10, i);
      bg.fillStyle(Phaser.Display.Color.GetColor(ci.r, ci.g, ci.b), 1);
      bg.fillCircle(cx, cy + h * 0.06, (Math.max(w, h) * 0.7) * (1 - t * 0.55));
    }

    // Rotating golden light rays behind the hero.
    this.rays = this.add.graphics().setDepth(-8).setPosition(cx, cy + h * 0.06);
    const rayCount = 14;
    for (let i = 0; i < rayCount; i++) {
      const a0 = (i / rayCount) * Math.PI * 2;
      const a1 = a0 + (Math.PI * 2 / rayCount) * 0.5;
      const R = Math.max(w, h);
      this.rays.fillStyle(PALETTE.sun, i % 2 ? 0.06 : 0.11);
      this.rays.fillTriangle(0, 0, Math.cos(a0) * R, Math.sin(a0) * R, Math.cos(a1) * R, Math.sin(a1) * R);
    }
    this.tweens.add({ targets: this.rays, angle: 360, duration: 26000, repeat: -1, ease: 'Linear' });

    // Pedestal + the athlete celebrating on it.
    const groundY = h * 0.9;
    this.add.graphics().setDepth(1)
      .fillStyle(PALETTE.sun, 1).fillRoundedRect(cx - 92 * s, groundY, 184 * s, 70 * s, 8)
      .fillStyle(0xffe9a8, 1).fillRect(cx - 92 * s, groundY, 184 * s, 8)
      .lineStyle(3, 0xffffff, 0.4).strokeRoundedRect(cx - 92 * s, groundY, 184 * s, 70 * s, 8);
    this.add.text(cx, groundY + 35 * s, '1', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(44 * s)}px`, color: '#7a5b12', fontStyle: 'bold' }).setOrigin(0.5).setDepth(2);

    const athlete = findAthlete([...SaveManager.instance.roster], this.result.athleteId);
    if (athlete) {
      const dispH = Phaser.Math.Clamp(h * 0.5, 220, 440);
      this.rig = createPortrait(this, cx, groundY + 6, athlete, dispH);
      this.rig.container.setDepth(3);
      this.rig.setPose('celebrate');
      // Triumphant little hops.
      this.tweens.add({ targets: this.rig.container, y: groundY + 6 - 26 * s, duration: 520, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
    }

    // Headline.
    const title = this.add.text(cx, h * 0.16, t('res.worldRecord'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(28, Math.round(48 * s))}px`, color: '#ffd24a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(20).setScale(0).setShadow(0, 6, '#000000aa', 10);
    this.tweens.add({ targets: title, scale: 1, duration: 560, delay: 200, ease: 'Back.out' });
    this.tweens.add({ targets: title, angle: { from: -2.5, to: 2.5 }, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.inOut', delay: 760 });

    // Discipline + athlete line, and the headline metric.
    this.add.text(cx, h * 0.16 + Math.max(30, 44 * s), `${disciplineLabel(this.result.disciplineId)} · ${countryFlag(this.result.nationality)} ${this.result.athleteName}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(15, Math.round(20 * s))}px`, color: '#cfe0ff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(20);
    const metric = this.add.text(cx, h * 0.34, formatMetric(this.result.disciplineId, this.result.metricKind, this.result.metric), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(34, Math.round(64 * s))}px`, color: '#6ef0a0', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(20).setAlpha(0).setShadow(0, 6, '#000000aa', 10);
    this.tweens.add({ targets: metric, alpha: 1, scale: { from: 0.6, to: 1 }, duration: 500, delay: 520, ease: 'Back.out' });

    this.celebrateFx(w, h, cx);
    this.time.delayedCall(260, () => { AudioEngine.fanfare(); AudioEngine.chime(); });
    this.time.delayedCall(900, () => AudioEngine.crowd());

    // Continue: tap anywhere, or auto-advance.
    const hint = this.add.text(cx, h * 0.92, t('rec.tapContinue'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(15 * s))}px`, color: '#9fb0e0',
    }).setOrigin(0.5).setDepth(20).setAlpha(0);
    this.tweens.add({ targets: hint, alpha: 0.9, duration: 600, delay: 1600, yoyo: true, repeat: -1 });
    this.input.once(Phaser.Input.Events.POINTER_DOWN, () => this.advance());
    this.time.delayedCall(5200, () => this.advance());

    // Optional hero clip layer (per star athlete) — no-op until assets exist.
    this.tryHeroClip(athlete?.species);
  }

  private celebrateFx(w: number, h: number, cx: number): void {
    if (!this.textures.exists('confetti')) return;
    // Continuous confetti rain.
    this.add.particles(0, -20, 'confetti', {
      x: { min: 0, max: w }, y: -20,
      lifespan: 3200, speedY: { min: 120, max: 260 }, speedX: { min: -50, max: 50 },
      angle: { min: 0, max: 360 }, rotate: { min: 0, max: 360 },
      scale: { start: 1, end: 0.4 }, alpha: { start: 1, end: 0.6 }, gravityY: 120,
      frequency: 30, tint: [0xffd24a, 0xff7a59, 0x59d2ff, 0x4ade80, 0xffffff],
    }).setDepth(15);
    // Gold burst from the hero on entrance.
    this.time.delayedCall(240, () => {
      this.add.particles(cx, h * 0.62, 'confetti', {
        lifespan: 1700, speed: { min: 220, max: 520 }, angle: { min: 0, max: 360 },
        scale: { start: 1.2, end: 0.3 }, alpha: { start: 1, end: 0 }, gravityY: 320,
        tint: [0xffd24a, 0xfff4c2, 0xffffff], emitting: false,
      }).setDepth(16).explode(54);
    });
    this.cameras.main.flash(380, 255, 240, 180);
    this.cameras.main.shake(220, 0.004);
  }

  /** Hook for a per-athlete Higgsfield "hero" clip, streamed on demand. No-op until present. */
  private tryHeroClip(_species?: string): void {
    // Future: if a record_<species> video/image asset is available, fade it in over the rig.
  }

  private advance(): void {
    if (this.advanced) return;
    this.advanced = true;
    this.cameras.main.fadeOut(220, 7, 10, 22);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.scene.start('Results', this.result));
  }

  update(_t: number, delta: number): void {
    this.rig?.update(delta / 1000);
  }
}
